import { action, makeAutoObservable } from 'mobx'
import { Value as RecipientsSelectorValue } from 'components/ShareSheet/RecipientSelector/Types'
import { Accessor } from 'components/ShareSheet/Types'
import { Access, FileAccessGrant, FileAccessGrantAccessor, Permission, AssetAccessGrantWithParent } from 'types'

/**
 * Store to manage the ShareSheetInfo
 */
class ShareSheetStore {
  /**
   * The asset the share sheet is for
   */
  asset: AssetAccessGrantWithParent | null
  /**
   * The highest access of the current user
   */
  currentUserHighestAccess: Access | undefined
  /**
   * The highest inherited access of the current user
   */
  currentUserInheritedAccessGrant: FileAccessGrant | null
  /**
   * The inherited accesses for the asset
   */
  inheritedAccesses: FileAccessGrant[]
  /**
   * The direct accesses for the asset
   */
  invitedAccesses: FileAccessGrant[]
  /**
   * The public accesses for the asset
   */
  publicAccess: FileAccessGrant | undefined
  /**
   * The inherited public access for the asset
   */
  inheritedPublicAccess: FileAccessGrant | undefined
  /**
   * Whether the user is taking ownership of the asset
   */
  takeOwnership: boolean
  /**
   * The other user the current user is transferring ownership to
   */
  transferOwnershipUser: Accessor | null
  /**
   * The message to send to the user when sharing
   */
  inviteMessage: string
  /**
   * Who is being given access now
   */
  recipients: RecipientsSelectorValue[]
  /**
   * What permission is being given now
   */
  selectedPermission: Permission
  /**
   * Whether the permissions are currently being submitted
   */
  isSubmitting: boolean
  /**
   * Whether new users should be added as workspace members
   */
  inviteToWorkspace: boolean
  /**
   * Whether we need to show the invite to workspace screen
   */
  showInviteToWorkspaceScreen: boolean
  /**
   * Whether the footer has been collapsed
   */
  footerOpen: boolean
  /**
   * The editors on the main layer of a layer
   */
  mainLayerEditors: FileAccessGrantAccessor[]
  /**
   * The id of the created review request
   */
  reviewScreenTitle: string | null

  constructor() {
    this.asset = null
    this.currentUserHighestAccess = undefined
    this.currentUserInheritedAccessGrant = null
    this.inheritedAccesses = []
    this.invitedAccesses = []
    this.publicAccess = undefined
    this.inheritedPublicAccess = undefined
    this.takeOwnership = false
    this.transferOwnershipUser = null
    this.inviteMessage = ''
    this.recipients = []
    this.selectedPermission = Permission.Edit
    this.isSubmitting = false
    this.inviteToWorkspace = false
    this.showInviteToWorkspaceScreen = false
    this.footerOpen = true
    this.mainLayerEditors = []
    this.reviewScreenTitle = null
    makeAutoObservable(this)
  }

  setShareSheet = action((state: Partial<ShareSheetStore>) => {
    Object.assign(this, state)
  })
}

const shareSheetStore = new ShareSheetStore()

export default shareSheetStore
